/**
 * Sync Status Indicator
 * Shows offline status and pending sync count
 */

import { useEffect, useRef, useState } from 'react'
import {
  Cloud,
  CloudOff,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  FileText,
  X,
} from 'lucide-react'
import { useOnlineStatus, useSyncStatus, useSync, usePendingVisitNotes } from '@/hooks/useOffline'
import { Button } from '@/components/ui'

interface SyncStatusIndicatorProps {
  showDetails?: boolean
  position?: 'top-right' | 'bottom-right' | 'bottom-left'
}

export function SyncStatusIndicator({
  showDetails = false,
  position = 'bottom-right',
}: SyncStatusIndicatorProps) {
  const isOnline = useOnlineStatus()
  const syncStatus = useSyncStatus()
  const { sync, isSyncing, canSync } = useSync()
  const { pendingNotes, count: pendingCount } = usePendingVisitNotes()
  const [isExpanded, setIsExpanded] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'bottom-right': 'bottom-20 right-4',
    'bottom-left': 'bottom-20 left-4',
  }

  useEffect(() => {
    if (!isExpanded) return

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsExpanded(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [isExpanded])

  // Don't show if online and no pending items
  if (isOnline && pendingCount === 0 && !syncStatus.isSyncing) {
    return null
  }

  return (
    <div ref={containerRef} className={`fixed ${positionClasses[position]} z-50`}>
      {/* Collapsed indicator */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-full shadow-lg
          transition-all duration-200
          ${isOnline
            ? pendingCount > 0
              ? 'bg-amber-500 text-white'
              : 'bg-green-500 text-white'
            : 'bg-gray-600 text-white'
          }
        `}
      >
        {isOnline ? (
          syncStatus.isSyncing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : pendingCount > 0 ? (
            <Cloud className="h-4 w-4" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )
        ) : (
          <CloudOff className="h-4 w-4" />
        )}

        <span className="text-sm font-medium">
          {isOnline
            ? syncStatus.isSyncing
              ? 'Syncing...'
              : pendingCount > 0
                ? `${pendingCount} pending`
                : 'Synced'
            : 'Offline'
          }
        </span>

        {(showDetails || pendingCount > 0) && (
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="absolute bottom-full right-0 mb-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/70">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Sync Status</h3>
            <button
              onClick={() => setIsExpanded(false)}
              className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 space-y-3">
            {/* Connection status */}
            <div className="flex items-center gap-2">
              {isOnline ? (
                <>
                  <div className="h-2 w-2 bg-green-500 rounded-full" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Connected</span>
                </>
              ) : (
                <>
                  <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Offline - changes saved locally</span>
                </>
              )}
            </div>

            {/* Pending items */}
            {pendingCount > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/70 dark:bg-amber-950/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-300" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {pendingCount} shift note{pendingCount !== 1 ? 's' : ''} pending
                    </p>
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                      {isOnline
                        ? 'Click sync to upload now'
                        : 'Will sync automatically when online'
                      }
                    </p>
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  {pendingNotes.slice(0, 3).map((note) => (
                    <div
                      key={note.id}
                      className="rounded-md border border-amber-200/80 bg-white/70 p-2 dark:border-amber-700/60 dark:bg-gray-950/40"
                    >
                      <div className="flex items-start gap-2">
                        <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700 dark:text-amber-300" />
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-amber-900 dark:text-amber-100">
                            Shift note for visit {shortId(note.data.visitId)}
                          </p>
                          <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-300">
                            Saved {formatRelativeTime(new Date(note.createdAt))}
                            {note.retryCount > 0 ? ` • ${note.retryCount} retry${note.retryCount !== 1 ? 's' : ''}` : ''}
                          </p>
                          {note.error && (
                            <p className="mt-1 line-clamp-2 text-[11px] text-red-700 dark:text-red-200">
                              {note.error}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {pendingNotes.length > 3 && (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      +{pendingNotes.length - 3} more shift note{pendingNotes.length - 3 !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Last sync */}
            {syncStatus.lastSyncAt && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Last synced: {formatRelativeTime(syncStatus.lastSyncAt)}
              </div>
            )}

            {/* Errors */}
            {syncStatus.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800/70 dark:bg-red-950/20">
                <p className="text-xs text-red-700 dark:text-red-200">
                  {syncStatus.errors.length} error{syncStatus.errors.length !== 1 ? 's' : ''} during sync
                </p>
              </div>
            )}

            {/* Actions */}
            {isOnline && pendingCount > 0 && (
              <Button
                onClick={sync}
                disabled={isSyncing || !canSync}
                size="sm"
                className="w-full"
              >
                {isSyncing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Now
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Compact inline indicator for use in headers
export function SyncStatusBadge() {
  const isOnline = useOnlineStatus()
  const { pendingCount } = useSyncStatus()

  if (isOnline && pendingCount === 0) {
    return null
  }

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
        ${isOnline
          ? 'bg-amber-100 text-amber-700'
          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
        }
      `}
    >
      {isOnline ? (
        <Cloud className="h-3 w-3" />
      ) : (
        <CloudOff className="h-3 w-3" />
      )}
      {isOnline ? `${pendingCount} pending` : 'Offline'}
    </div>
  )
}

function shortId(id: string): string {
  if (!id) return 'unknown'
  return id.length > 8 ? `${id.slice(0, 8)}...` : id
}

// Helper function to format relative time
function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)

  if (diffSec < 60) {
    return 'just now'
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`
  } else {
    return date.toLocaleDateString()
  }
}
